import PlayerConstants from "../../../shared/constants/player";
import errorConstants from "../../../shared/constants/error";
import { getAudioTrackId, getProgress, formatSecondsToTimeDisplay } from "../../../shared/utils/playerUtils";
import { shakaSetVideoPlayerControlState } from "./shakaPlayerControls";
import { playerGetCurrentPlaybackTime, shakaGetHlsLivePlayheadTs } from "./playerCoreUtils";
import { isPastProgram } from "../../../shared/utils";
import { setLocalStorage } from "../../../shared/utils/localStorage";
import { removeSessionStorage } from "../../../shared/utils/sessionStorage";
import constants from "../../../shared/constants";
import storageConstants from "../../../shared/constants/storage";
import { trackMediaComplete, trackMediaBitrateChange, trackPlayerClosedCaption } from "../../../shared/analytics/media";
import { isTimestamp } from "../../../shared/utils/playbackHelpers";
import { usePlayerActionUtils, updateOnDemandProgress } from "../core/playerUtils";

const { SETTINGS_CONFIG } = constants;
const { PLAYER_CONTROLS, PLAYBACK_TYPES, BINGE_WATCH_TIMER, BOOKMARK_UPDATE_INTERVAL_TIMER } = PlayerConstants;
const { ERROR_TYPES } = errorConstants;
const { PLAYER_LOADED } = storageConstants;

const usePlayerEventHandlers = () => {
  const { toggleSpinningLoader, displayToastNotification, updateVideoPlayingState } = usePlayerActionUtils();

  const playerInitOnDemandBookmark = (
    playbackType,
    isOnDemandType,
    bookmarkTimer,
    bookmarkCreated,
    updateBookmarkInfo,
    createBookmarkInfo,
    hasBookmark
  ) => {
    if (playbackType !== PLAYBACK_TYPES.TRAILER && isOnDemandType) {
      if (!bookmarkTimer.current) {
        bookmarkTimer.current = setInterval(() => {
          hasBookmark.current = 1;
          if (bookmarkCreated.current) {
            updateBookmarkInfo();
          } else {
            createBookmarkInfo().then((res) => {
              bookmarkCreated.current = res?.bookmark;
            });
          }
        }, BOOKMARK_UPDATE_INTERVAL_TIMER);
      }
    }
  };

  // Set Initial FF Status Settings
  const playerInitFFStatus = (
    isOnDemandType,
    isCatchUpAllowed,
    setVideoStarted,
    activeExtendedMetadata,
    setFFEnabled,
    isRestartTrickPlayAllowed,
    setFFErrorStatus,
    isFFEnabled
  ) => {
    if (isOnDemandType || isCatchUpAllowed) {
      setVideoStarted(true);
      if (isOnDemandType && activeExtendedMetadata.current) {
        setFFEnabled(!activeExtendedMetadata.current.fwdBlocked);
      } else if (isCatchUpAllowed) {
        setFFEnabled(isRestartTrickPlayAllowed.current);
      } else {
        setFFEnabled(true);
      }
    }
    setFFErrorStatus(!isFFEnabled);
  };

  // Set Initial RW Status Settings
  const playerInitRWStatus = (isOnDemandType, activeExtendedMetadata, setRWEnabled, isCatchUpAllowed) => {
    if (isOnDemandType) {
      if (activeExtendedMetadata.current) {
        setRWEnabled(!activeExtendedMetadata.current.rwdBlocked);
      } else {
        setRWEnabled(true);
      }
    } else if (isCatchUpAllowed) {
      setRWEnabled(true);
    }
  };

  const playerCheckStartOverStatus = (isRestartedStream, isLookbackStream, startingOverStream) => {
    if (isRestartedStream || isLookbackStream) {
      startingOverStream.current = false;
    }
  };

  const handleRetryAndPlayerError = (
    isPlaybackRetry,
    videoProgressMade,
    playerLoaderClassName,
    playerMgr,
    createRetryTimer,
    setShowBgImage,
    controlPlaybackDeltaTimer,
    ffErrorStatus,
    eventObj,
    setFFErrorStatus,
    onPlaybackStateChange,
    isRetryPlayback,
    handleAVSStopContent,
    activeContentId
  ) => {
    if (isPlaybackRetry?.enabled && videoProgressMade.current && isRetryPlayback()) {
      toggleSpinningLoader(true, playerLoaderClassName);

      // Remove loaded content
      if (playerMgr.current) {
        playerMgr.current.unload();
      }

      // Attempt to fetch a new JWT and manifest
      createRetryTimer();
    } else {
      handleAVSStopContent(true, activeContentId);
      setShowBgImage(false);
      controlPlaybackDeltaTimer(false);
      const eventErrorCode = eventObj.data?.[1];
      if (ffErrorStatus && eventErrorCode === 226) {
        setFFErrorStatus(false);
        return;
      }
      toggleSpinningLoader(false);

      if (onPlaybackStateChange) {
        onPlaybackStateChange({
          errorType: ERROR_TYPES.ERROR,
          message: eventObj.message,
          code: eventObj.code,
        });
      }
    }
  };

  const shakaInitPlaybackSettings = (
    type,
    currentContentId,
    assetId,
    videoProgressMade,
    isSafari,
    playerMgr,
    setShowBgImage,
    controlPlaybackDeltaTimer
  ) => {
    updateVideoPlayingState({
      playing: true,
      type: type.toUpperCase(),
      currentContentId: currentContentId,
      assetId: assetId,
    });
    videoProgressMade.current = true;
    if (isSafari) {
      playerMgr.current.unmute();
    }
    setShowBgImage(false);
    controlPlaybackDeltaTimer(true);
  };

  const shakaInitADSetting = (playerMgr, isDescribedVideoEnabled, setADAvailable, hasADAudioTrack) => {
    const availableAudioTracks = playerMgr.current?.getAudioTracks();
    if (availableAudioTracks) {
      playerMgr.current.setAudioTrack(getAudioTrackId(availableAudioTracks, isDescribedVideoEnabled.current));
      setADAvailable(hasADAudioTrack(playerMgr.current.getVariantTracks()));
    }
  };

  const shakaInitCCSetting = (playerMgr, setIsCCAvailable, isClosedCaptionEnabled) => {
    const hasCaptions = playerMgr.current?.hasCaptions();

    if (hasCaptions) {
      setIsCCAvailable(true);
    } else {
      setIsCCAvailable(false);
    }

    const isCCEnabled = playerMgr.current?.isCaptionsEnabled();
    isClosedCaptionEnabled.current = isCCEnabled;
    setLocalStorage(SETTINGS_CONFIG.CLOSED_CAPTION, isCCEnabled);
  };

  const playerInitOnNowRecordingPlayback = (
    hasRecordingDeltaTime,
    isDynamicAsset,
    recordingLiveAssetStarted,
    playerMgr,
    userRecordingStopTime,
    recordingPlaybackDuration,
    recordingLiveAssetStartTime,
    setElapsedTime
  ) => {
    if (hasRecordingDeltaTime && isDynamicAsset && !recordingLiveAssetStarted.current) {
      const currentPlayhead = playerMgr.current?.getCurrentTime(); // Warning: DO NOT use eventObj.time to calculate dynamic playhead. In Playing event, eventObj.time is not accurate for HLS dynamic partial recording playback so we need call getCurrentTime() to get the accurate playhead
      if (playerMgr.current?.isLive()) {
        recordingPlaybackDuration.current =
          userRecordingStopTime / 1000 - Number.parseFloat(currentPlayhead).toFixed(3);
      }
      recordingLiveAssetStartTime.current = Number.parseFloat(currentPlayhead).toFixed(3);
      setElapsedTime("");
      recordingLiveAssetStarted.current = true;
    }
  };

  const shakaInitHlsOnNowRecordingPlayback = (
    hasRecordingDeltaTime,
    isDynamicAsset,
    recordingLiveAssetStarted,
    playerMgr,
    userRecordingStopTime,
    recordingPlaybackDuration,
    recordingLiveAssetStartTime,
    setElapsedTime
  ) => {
    if (hasRecordingDeltaTime && isDynamicAsset && !recordingLiveAssetStarted.current) {
      const currentPlayhead = shakaGetHlsLivePlayheadTs(playerMgr); // Warning: DO NOT use eventObj.time to calculate dynamic playhead. In Playing event, eventObj.time is not accurate for HLS dynamic partial recording playback so we need call getCurrentTime() to get the accurate playhead
      recordingPlaybackDuration.current =
        userRecordingStopTime / 1000 - Number.parseFloat(currentPlayhead / 1000).toFixed(3);
      recordingLiveAssetStartTime.current = Number.parseFloat(currentPlayhead / 1000).toFixed(3);
      setElapsedTime("");
      recordingLiveAssetStarted.current = true;
    }
  };

  const playerClearLiveAutoPlayTimer = (autoPlayLiveTimer) => {
    clearTimeout(autoPlayLiveTimer.current);
    autoPlayLiveTimer.current = null;
  };

  const shakaSetLiveAutoPlayTimer = (
    isPauseLiveAllowed,
    isRestartedStream,
    isLookbackStream,
    pauseLiveProperties,
    autoPlayLiveTimer,
    playerMgr,
    setPlayPauseIcon,
    videoControlsMouseMove
  ) => {
    if (isPauseLiveAllowed.current && !isRestartedStream && !isLookbackStream && pauseLiveProperties?.pausePeriodMins) {
      autoPlayLiveTimer.current = setTimeout(() => {
        shakaSetVideoPlayerControlState(playerMgr.current, PLAYER_CONTROLS.RESUME);
        setPlayPauseIcon("/images/playback-pause.svg");
        displayToastNotification("pltv_resume_text");
        videoControlsMouseMove();
      }, pauseLiveProperties.pausePeriodMins * 60 * 1000);
    }
  };

  const playerHandleStartOverTransition = (
    isRestartedStream,
    isLookbackStream,
    nextLiveProgramDetails,
    triggerLookback,
    exitLookback,
    liveProgramCanUpdate,
    onPlaybackStateChange
  ) => {
    if (isRestartedStream || isLookbackStream) {
      if (isPastProgram(nextLiveProgramDetails.current?.metadata?.airingEndTime)) {
        triggerLookback(nextLiveProgramDetails.current); // transition into lookback mode if next program is already aired
      } else {
        exitLookback(); // transition out of lookback mode for a stream currently live
        liveProgramCanUpdate.current = true;
        onPlaybackStateChange(PLAYER_CONTROLS.GET_NEXT_LIVE_PROGRAM);
      }
    }
  };

  /*
  When playing the user specific portion of recording playback, the progress calculation is relative to recordingStartDeltaTime && recordingStopDeltaTime.
  This is distinguished from player playback progress.
  */
  const playerSetRecordingProgress = (
    targetTime,
    recordingStartDeltaTime,
    recordingPlaybackDuration,
    setElapsedTime,
    setRemainingTime,
    setProgressStyle,
    updateVodProgress,
    isDynamicAsset
  ) => {
    const recordingPlaybackSeekTarget = parseInt(targetTime - recordingStartDeltaTime);
    const progress = getProgress(recordingPlaybackSeekTarget, recordingPlaybackDuration.current);
    if (!isDynamicAsset) {
      setElapsedTime(formatSecondsToTimeDisplay(recordingPlaybackSeekTarget));
    }
    setRemainingTime(recordingPlaybackDuration.current - recordingPlaybackSeekTarget);
    updateOnDemandProgress(progress, setProgressStyle, updateVodProgress);
  };

  const shakaSetVodProgress = (
    targetTime,
    playerMgr,
    isRestartedStream,
    setElapsedTime,
    setRemainingTime,
    setProgressStyle,
    updateVodProgress
  ) => {
    const progress = getProgress(parseInt(targetTime), playerMgr.current?.getDuration());
    if (!isRestartedStream) {
      setElapsedTime(formatSecondsToTimeDisplay(targetTime));
      setRemainingTime(playerMgr.current?.getDuration() - parseInt(targetTime));
    }
    updateOnDemandProgress(progress, setProgressStyle, updateVodProgress);
  };

  const playerHandleRecordingProgressUpdate = (
    eventObj,
    recordingStartDeltaTime,
    recordingPlaybackDuration,
    recordingLiveAssetStartTime,
    setElapsedTime,
    setRemainingTime,
    playerMgr,
    setPlayPauseIcon,
    history,
    isDynamicAsset,
    recordingStopDeltaTime,
    userRecordingStopTime,
    isTimeShifting,
    setProgressStyle,
    updateVodProgress
  ) => {
    let recordingPlaybackProgress = 0;
    if (!isDynamicAsset()) {
      recordingPlaybackProgress =
        eventObj.time - recordingStartDeltaTime > 0 ? eventObj.time - recordingStartDeltaTime : 0;
    } else {
      if (recordingLiveAssetStartTime.current > 0) {
        recordingPlaybackProgress =
          Math.floor(eventObj.time) - recordingLiveAssetStartTime.current > 0
            ? Math.floor(eventObj.time) - recordingLiveAssetStartTime.current
            : 0;
      }
    }
    const progress = getProgress(
      recordingPlaybackProgress > 0 ? recordingPlaybackProgress : 0,
      recordingPlaybackDuration.current
    );

    if (
      (!isDynamicAsset() && eventObj.time > recordingStopDeltaTime) ||
      (isDynamicAsset() && eventObj.time > userRecordingStopTime / 1000)
    ) {
      if (playerMgr.current?.isPlaying()) {
        shakaSetVideoPlayerControlState(playerMgr.current, PLAYER_CONTROLS.STOP);
        setPlayPauseIcon("/images/playback-play.svg");
        removeSessionStorage(PLAYER_LOADED);
        trackMediaComplete();
        if (1 < history.length) {
          history.goBack();
        } else {
          history.push("/");
        }
      }
    }

    if (!isTimeShifting.current) {
      setElapsedTime(formatSecondsToTimeDisplay(recordingPlaybackProgress));
      setRemainingTime(recordingPlaybackDuration.current - recordingPlaybackProgress);
      updateOnDemandProgress(progress, setProgressStyle, updateVodProgress);
    }
  };

  const shakaHlsOnNowRecordingProgressUpdate = (
    event,
    recordingStartDeltaTime,
    recordingPlaybackDuration,
    setElapsedTime,
    setRemainingTime,
    playerMgr,
    setPlayPauseIcon,
    history,
    recordingStopDeltaTime,
    isTimeShifting,
    setProgressStyle,
    updateVodProgress
  ) => {
    let recordingPlaybackProgress = 0;
    if (recordingStartDeltaTime > 0) {
      recordingPlaybackProgress =
        Math.floor(event.time) - recordingStartDeltaTime > 0 ? Math.floor(event.time) - recordingStartDeltaTime : 0;
    }

    const progress = getProgress(
      recordingPlaybackProgress > 0 ? recordingPlaybackProgress : 0,
      recordingPlaybackDuration.current
    );

    if (event.time > recordingStopDeltaTime) {
      if (playerMgr.current?.isPlaying()) {
        shakaSetVideoPlayerControlState(playerMgr.current, PLAYER_CONTROLS.STOP);
        setPlayPauseIcon("/images/playback-play.svg");
        removeSessionStorage(PLAYER_LOADED);
        trackMediaComplete();
        if (1 < history.length) {
          history.goBack();
        } else {
          history.push("/");
        }
      }
    }

    if (!isTimeShifting.current) {
      setElapsedTime(formatSecondsToTimeDisplay(recordingPlaybackProgress));
      setRemainingTime(recordingPlaybackDuration.current - recordingPlaybackProgress);
      updateOnDemandProgress(progress, setProgressStyle, updateVodProgress);
    }
  };

  const playerHandleProgressUpdate = (
    eventObj,
    playerMgr,
    isRestartedStream,
    setElapsedTime,
    setRemainingTime,
    isDynamicAsset,
    endTime,
    props,
    setProgressStyle,
    updateVodProgress
  ) => {
    if (playerMgr.current?.getDuration()) {
      let newPlayheadTime = eventObj.time;
      if (isDynamicAsset()) {
        const currentSource = playerMgr.current.getSource();
        newPlayheadTime = playerGetCurrentPlaybackTime(playerMgr, currentSource) / 1000;
      }

      const progress = getProgress(
        isDynamicAsset() ? props.metadataInfo?.duration - (endTime / 1000 - newPlayheadTime) : newPlayheadTime,
        isDynamicAsset() ? props.metadataInfo?.duration : playerMgr.current.getDuration()
      );

      if (!isRestartedStream) {
        const elapsedTime = isDynamicAsset()
          ? props.metadataInfo?.duration - (endTime / 1000 - newPlayheadTime)
          : eventObj.time;
        setElapsedTime(formatSecondsToTimeDisplay(elapsedTime));
        const remainingTime = (isDynamicAsset() ? endTime / 1000 : playerMgr.current.getDuration()) - newPlayheadTime;
        setRemainingTime(remainingTime);
      }

      updateOnDemandProgress(progress, setProgressStyle, updateVodProgress);
    }
  };

  const playerHandleSocuLiveTransition = (
    eventObj,
    isRestartedStream,
    isLookbackStream,
    liveProgramDetails,
    playerMgr,
    fetchNextLiveStream,
    nextLiveProgramDetails,
    isNextLiveStreamFetched,
    handlePlaybackFinishedEvent,
    setIsReadyToChangeStreams
  ) => {
    /**
     * Modifying restarted live stream transition to fetch the next stream towards the end of the stream instead of the beginning,
     * to avoid all the edge cases that can come up due to stale information in case user spends long time on one stream using pause and play.
     * Using the binge watch timer value for the transition, next stream to be fetched during last 20 seconds of the current restarted stream.
     */
    if (isRestartedStream || isLookbackStream) {
      const programEndTime = isTimestamp(eventObj.time)
        ? liveProgramDetails.metadata.airingEndTime / 1000
        : playerMgr.current?.getDuration();
      if (
        isNextLiveStreamFetched.current === false &&
        programEndTime - eventObj.time < BINGE_WATCH_TIMER &&
        eventObj.time != 0
      ) {
        fetchNextLiveStream(liveProgramDetails).then((nextLiveProgram) => {
          nextLiveProgramDetails.current = nextLiveProgram;
          if (isRestartedStream) {
            // TSQA-43868 Finish event is not always triggered at the end of the restart content, we need to force this event
            setIsReadyToChangeStreams && setIsReadyToChangeStreams(false);
            handlePlaybackFinishedEvent();
          }
        });
        isNextLiveStreamFetched.current = true;
      }
    }
  };

  const shakaHandleQualityChange = (event, playerMgr) => {
    const { droppedFrames, bitrate } = playerMgr.current.getStats();
    trackMediaBitrateChange({
      bitrate,
      fps: event.fps,
      droppedFrames,
    });
  };

  const playerHandleOnNowRecordingTimeShift = (
    targetTime,
    recordingLiveAssetStartTime,
    recordingPlaybackDuration,
    setElapsedTime,
    setRemainingTime,
    setProgressStyle,
    shakaIsPlaybackStarted
  ) => {
    /*
    if an "on now" recording playback is in time shifting, we need to calculate the playback progress relative to user's recordingStartDeltaTime, and se the progress accordingly. When this happens, we also disable the progress calculation in TimeChanged event to prevent progress bar from shifting back
    */
    const recordingPlaybackProgress = Math.floor(targetTime) - recordingLiveAssetStartTime.current;
    const progress = getProgress(recordingPlaybackProgress, recordingPlaybackDuration.current);
    if (progress > 0 && progress < 100 && shakaIsPlaybackStarted.current) {
      setElapsedTime(formatSecondsToTimeDisplay(recordingPlaybackProgress));
      setRemainingTime(recordingPlaybackDuration.current - recordingPlaybackProgress);
      setProgressStyle(progress);
    }
  };

  const shakaHandleSubtitleChange = (
    isClosedCaptionEnabled,
    playerMgr,
    event,
    signalUserActivity,
    isDynatraceTrackingEnabled,
    shouldTrackAnalytics = true // Default true for user-initiated actions
  ) => {
    isClosedCaptionEnabled.current = event;
    setLocalStorage(SETTINGS_CONFIG.CLOSED_CAPTION, event);

    if (playerMgr.current?.hasCaptions()) {
      const availableCaptions = playerMgr.current.getCaptionTracks();
      if (availableCaptions?.[0]?.id) {
        if (isClosedCaptionEnabled.current) {
          playerMgr.current.enableCaptions(availableCaptions[0].id);
          // Only track analytics for user-initiated actions, not Safari HLS workaround
          if (shouldTrackAnalytics) {
            trackPlayerClosedCaption(true, isDynatraceTrackingEnabled);
          }
        } else {
          playerMgr.current.disableCaptions();
          // Only track analytics for user-initiated actions, not Safari HLS workaround
          if (shouldTrackAnalytics) {
            trackPlayerClosedCaption(false, isDynatraceTrackingEnabled);
          }
        }
      }
    }

    signalUserActivity();
  };

  return {
    playerInitOnDemandBookmark,
    playerInitFFStatus,
    playerInitRWStatus,
    playerCheckStartOverStatus,
    handleRetryAndPlayerError,
    shakaInitPlaybackSettings,
    shakaInitADSetting,
    shakaInitCCSetting,
    playerInitOnNowRecordingPlayback,
    shakaInitHlsOnNowRecordingPlayback,
    playerClearLiveAutoPlayTimer,
    shakaSetLiveAutoPlayTimer,
    playerHandleStartOverTransition,
    playerSetRecordingProgress,
    shakaSetVodProgress,
    playerHandleRecordingProgressUpdate,
    shakaHlsOnNowRecordingProgressUpdate,
    playerHandleProgressUpdate,
    playerHandleSocuLiveTransition,
    shakaHandleQualityChange,
    playerHandleOnNowRecordingTimeShift,
    shakaHandleSubtitleChange,
  };
};

export default usePlayerEventHandlers;
